import { FC, ReactNode, useState } from 'react'
import { Descriptions, Divider, Drawer, Flex, Table, Tag, Typography, theme } from 'antd'
import type { DescriptionsProps, TableColumnsType } from 'antd'
import type { CSSProperties } from 'react'
import Shipment, { windingsDisplay } from '../../../../types/api/shipment'
import ShipmentPosition from '../../../../types/api/shipment-position'
import { MaterialRequirement } from '../../../../types/api/components/material/material-requirements'
import { hasDmsInFinishing } from '../../../../helpers/is-dms-machine'
import StampImageLink from '../../../../components/stamp-image-link/stamp-image-link'
import formatIntegerBySpace from '../../../../helpers/formatters/format-integer-by-space'
import { formatRollerDisplay } from '../../../../helpers/format-roller-display'
import { styled } from 'styled-components'

const Text = styled(Typography.Text)`
    font-size: inherit;
`

const SectionDivider = styled(Divider)`
    margin: 20px 0 12px;

    &:first-child {
        margin-top: 0;
    }
`

const InfoDescriptions = styled(Descriptions)`
    .ant-descriptions-item-label {
        width: 40%;
    }
`

type DescriptionsItemType = NonNullable<DescriptionsProps['items']>[number]

interface ShipmentHeadingCellProps {
    shipment: Shipment
}

const commentTextStyle: CSSProperties = {
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
}

const detailsTriggerStyle = (color: string): CSSProperties => ({
    cursor: 'pointer',
    color,
    borderBottom: `1px dashed ${color}`,
})

function formatMachines(shipment: Shipment): string {
    const names = [
        shipment.machine?.name.split(' ')[0],
        ...(shipment.additional_machines?.map(x => x.name.split(' ')[0]) ?? []),
    ].filter(Boolean)

    return names.length ? names.join(', ') : '—'
}

function getPositionsWithComments(positions?: ShipmentPosition[]): ShipmentPosition[] {
    return positions?.filter(position => position.comment?.trim()) ?? []
}

function formatMaterialRequirement(requirement: MaterialRequirement): string {
    const name = requirement.material_type?.name || '—'
    const widths = [
        ...new Set(
            (requirement.configurations ?? [])
                .map(config => config.material?.width)
                .filter((width): width is number => width != null)
        ),
    ]

    return widths.length ? `${name} (${widths.join(', ')} мм)` : name
}

function getMaterialsDisplay(shipment: Shipment): { main?: string; additional: string[] } {
    const requirements = shipment.materialRequirements
    if (!requirements?.length) return { additional: [] }

    const [main, ...additional] = requirements
    return {
        main: formatMaterialRequirement(main),
        additional: additional.map(formatMaterialRequirement),
    }
}

function formatLacquers(shipment: Shipment): string {
    const items = shipment.lacquers
        ?.map(lacquer => [lacquer.type?.name, lacquer.name].filter(Boolean).join(' - '))
        .filter(Boolean)

    return items?.length ? items.join(', ') : '—'
}

/** Параметры штампа для отображения: при «Без высечки» — из virtualStamp; ручьи — менеджера (не штампа). */
function getStampDisplay(shipment: Shipment) {
    if (shipment.withoutCutting) {
        const virtualStamp = shipment.virtualStamp
        return {
            number: 'Без высечки',
            width: virtualStamp?.width,
            length: virtualStamp?.length,
            roller: formatRollerDisplay(virtualStamp?.roller?.roller),
            floods: virtualStamp?.floods,
            range: virtualStamp?.range,
        }
    }

    return {
        number: shipment.stamp?.number,
        width: shipment.stamp?.width,
        length: shipment.stamp?.length,
        roller: formatRollerDisplay(shipment.stamp?.roller?.roller),
        floods: shipment.stampFloods,
        range: shipment.stamp?.range,
    }
}

function YnTag({ value }: { value: boolean | undefined }) {
    if (value == null) return <Text type='secondary'>—</Text>
    return (
        <Tag color={value ? 'green' : 'default'} style={{ marginInlineEnd: 0 }}>
            {value ? 'Да' : 'Нет'}
        </Tag>
    )
}

function InfoSection({ title, items }: { title: string; items: DescriptionsProps['items'] }) {
    return (
        <>
            <SectionDivider orientation='left' orientationMargin={0}>
                {title}
            </SectionDivider>
            <InfoDescriptions bordered size='small' column={1} items={items} />
        </>
    )
}

function infoItem(label: string, value: ReactNode): DescriptionsItemType {
    return { key: label, label, children: value == null || value === '' ? '—' : value }
}

const positionColumns: TableColumnsType<ShipmentPosition> = [
    {
        title: 'Вид',
        render: (_v, position, index) => position.labelName || `Вид ${index + 1}`,
    },
    {
        title: 'Ручьи в сборке',
        dataIndex: 'floodsInAccumulation',
        render: (value?: number) => value ?? '—',
    },
    {
        title: 'Тираж',
        dataIndex: 'copies',
        render: (value?: number) => (value != null ? formatIntegerBySpace(value) : '—'),
    },
]

const ShipmentHeadingCell: FC<ShipmentHeadingCellProps> = ({ shipment }) => {
    const { token } = theme.useToken()
    const [detailsOpen, setDetailsOpen] = useState(false)
    const manager = shipment.user_audit?.created_by_permissions_user?.username
    const additionalJobs = shipment.additional_jobs?.map(x => x.name).join(', ')
    const commentPositions = getPositionsWithComments(shipment.shipment_positions)
    const hasComments = Boolean(shipment.comment?.trim()) || commentPositions.length > 0
    const materials = getMaterialsDisplay(shipment)
    const stamp = getStampDisplay(shipment)
    const stampSize =
        stamp.width != null && stamp.length != null ? `${stamp.width}x${stamp.length}` : undefined

    return (
        <Flex vertical gap={4}>
            <Flex gap={18} align='center' wrap='wrap'>
                <span style={{ fontSize: 16, fontWeight: 'bold' }}>
                    {shipment.client?.name || '—'}
                </span>

                <Text>Заказ №{shipment.id}</Text>

                {manager ? <Text>Менеджер: {manager}</Text> : null}
            </Flex>

            <Flex gap={18} align='center' wrap='wrap'>
                <Flex gap={4} align='center'>
                    <Text>Штамп: {stamp.number ?? '—'}</Text>
                    {!shipment.withoutCutting && shipment.stamp ? (
                        <StampImageLink stamp={shipment.stamp} />
                    ) : null}
                </Flex>

                <Text>Размеры (ШхД): {stampSize ?? '—'}</Text>

                <Text>Раппорт: {stamp.roller ?? '—'}</Text>

                <Text>Ручьи: {stamp.floods ?? '—'}</Text>

                <Text>М/э: {stamp.range ?? '—'}</Text>

                <Text>Станки: {formatMachines(shipment)}</Text>

                <Text>
                    Схема намотки (л/о): {windingsDisplay(shipment.windingSchemeFace)} /{' '}
                    {windingsDisplay(shipment.windingSchemeTurn)}
                </Text>

                {additionalJobs ? <Text>Доп. работы: {additionalJobs}</Text> : null}

                {shipment.shipment_positions?.length ? (
                    <Text>Видов: {shipment.shipment_positions.length}</Text>
                ) : null}

                <span
                    style={detailsTriggerStyle(token.colorWarning)}
                    onClick={() => setDetailsOpen(true)}
                >
                    Подробная информация
                </span>
            </Flex>

            <Drawer
                title={
                    <Flex justify='space-between' align='center' gap={16}>
                        <Text strong>Информация о заказе</Text>
                        <Text type='secondary'>
                            №{shipment.id}
                            {shipment.client?.name ? ` · ${shipment.client.name}` : ''}
                        </Text>
                    </Flex>
                }
                width={600}
                open={detailsOpen}
                onClose={() => setDetailsOpen(false)}
            >
                <Flex vertical>
                    <InfoSection
                        title='Заказ'
                        items={[
                            infoItem('Заказ №', shipment.id),
                            ...(manager ? [infoItem('Менеджер', manager)] : []),
                        ]}
                    />

                    <InfoSection
                        title='Материалы'
                        items={[
                            infoItem('Основной', materials.main),
                            ...materials.additional.map((item, index) =>
                                infoItem(
                                    materials.additional.length > 1 ? `Доп. ${index + 1}` : 'Доп.',
                                    item
                                )
                            ),
                        ]}
                    />

                    <InfoSection
                        title='Штамп'
                        items={[
                            infoItem('Штамп', stamp.number),
                            infoItem('Размеры (ШхД)', stampSize),
                            infoItem('Раппорт', stamp.roller),
                            infoItem('Ручьи', stamp.floods),
                            infoItem('М/э', stamp.range),
                        ]}
                    />

                    <InfoSection
                        title='Производство'
                        items={[
                            infoItem('Станки', formatMachines(shipment)),
                            ...(additionalJobs ? [infoItem('Доп. работы', additionalJobs)] : []),
                            ...(shipment.shipment_positions?.length
                                ? [infoItem('Видов', shipment.shipment_positions.length)]
                                : []),
                            infoItem(
                                'Схема намотки',
                                `${windingsDisplay(shipment.windingSchemeFace)} / ${windingsDisplay(shipment.windingSchemeTurn)}`
                            ),
                        ]}
                    />

                    {shipment.shipment_positions?.length ? (
                        <>
                            <SectionDivider orientation='left' orientationMargin={0}>
                                Позиции
                            </SectionDivider>
                            <Table
                                bordered
                                size='small'
                                pagination={false}
                                columns={positionColumns}
                                dataSource={shipment.shipment_positions}
                                rowKey={position => position.documentId || position.id}
                            />
                        </>
                    ) : null}

                    <InfoSection
                        title='Отделка'
                        items={[
                            infoItem('Конгрев', <YnTag value={shipment.congreve} />),
                            infoItem('Лаки', formatLacquers(shipment)),
                            infoItem('Праймировать материал', <YnTag value={shipment.priming} />),
                            infoItem(
                                'Отделка на DMS',
                                <YnTag value={hasDmsInFinishing(shipment.additional_machines)} />
                            ),
                        ]}
                    />

                    {hasComments ? (
                        <InfoSection
                            title='Комментарии'
                            items={[
                                ...(shipment.comment?.trim()
                                    ? [
                                          infoItem(
                                              'К отгрузке',
                                              <Text style={commentTextStyle}>{shipment.comment}</Text>
                                          ),
                                      ]
                                    : []),
                                ...commentPositions.map(position => ({
                                    ...infoItem(
                                        position.labelName || 'Позиция',
                                        <Text style={commentTextStyle}>{position.comment}</Text>
                                    ),
                                    key: position.documentId || position.id,
                                })),
                            ]}
                        />
                    ) : null}
                </Flex>
            </Drawer>
        </Flex>
    )
}

export default ShipmentHeadingCell
